import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DraftsRevokeModal from "../../../common/DraftsRevokeModal/DraftsRevokeModal";
import { ReactComponent as EditIcon } from "../../../resources/icons/edit.svg";
import { ReactComponent as FlyingMail } from "../../../resources/icons/flying-mail.svg";
import ToolTip from "../../../common/ToolTip/ToolTip";
import { FileText, FileImage } from "lucide-react";
import EntityConstants from "../../../configs/EntityConstants";
import { PUT } from "../../../service/CRUDService";
import Toast from "../../../common/Toast/Toast";

const ProgrammeBatchCard = (props) => {
  const {
    name,
    description,
    credential_template_id,
    issuer_credential_template_id,
    navigate_url,
    removeCredentialFromList,
    template_type,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(null);
  const navigate = useNavigate();

  const revokeCredential = async (issuer_credential_template_id) => {
    try {
      const response = await PUT(
        `${EntityConstants.PUT_REVOKE_DRAFT}?issuer_credential_template_id=${issuer_credential_template_id}`
      );
      const data = await response.json();
      if (response.ok) {
        removeCredentialFromList(issuer_credential_template_id);
        Toast({
          type: "success",
          title: "Success",
          message: `${data.details}`,
        });
      } else {
        console.log(data.error);
        Toast({
          type: "error",
          title: "Error",
          message: `${data.error}`,
        });
      }
    } catch (error) {
      console.error("Failed to revoke credential", error);
      Toast({
        type: "error",
        title: "Error",
        message: "An error occurred while revoking the credential.",
      });
    }
  };

  const handleCardKeyPress = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (credential_template_id && navigate_url) {
        localStorage.setItem("templateType", template_type);
        navigate(navigate_url);
      }
    }
  };

  const handleEditKeyPress = (
    e,
    credential_template_id,
    issuer_credential_template_id
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      navigate(
        `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`
      );
    }
  };

  const handleIssueKeyPress = (
    e,
    credential_template_id,
    issuer_credential_template_id
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (template_type === "Legacy") {
        localStorage.setItem("templateType", template_type);
        navigate(navigate_url);
      } else {
        navigate(
          `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}`
        );
      }
    }
  };

  return (
    <div
      className={`flex flex-col rounded-xl overflow-hidden hover:shadow-lg transition-shadow cursor-pointer bg-white border border-l-4 border-l-[#3538CD] h-[14rem] ${
        credential_template_id && "hover:scale-100"
      } transition-all duration-300`}
      name={credential_template_id}
      onClick={() => {
        if (credential_template_id && navigate_url) {
          localStorage.setItem("templateType", template_type);
          navigate(navigate_url);
        }
      }}
      onKeyDown={handleCardKeyPress}
      role="button"
      tabIndex={0}
    >
      <div className="p-6 flex flex-col justify-between h-full">
        <div className="flex flex-col h-full justify-between">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg pr-2 line-clamp-2">
              <span
                className={
                  !name
                    ? "h-6 w-full animate-pulse bg-zinc-300 rounded-md block"
                    : ""
                }
              >
                {name}
              </span>
            </h3>
            {template_type && (
              <span
                className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                  template_type === "SVG"
                    ? "bg-[#EEF2FF] text-[#4F46E5]"
                    : "bg-[#ffe8cb] text-[#de881f]"
                }`}
              >
                {template_type === "SVG" ? (
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3 text-cfPrimaryButton" />
                    <span>SVG</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <FileImage className="h-3 w-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG" />
                    <span>Legacy</span>
                  </div>
                )}
              </span>
            )}
          </div>

          <div className="text-gray-600 text-sm line-clamp-2">
            <span
              className={
                !description
                  ? "h-4 w-full animate-pulse bg-zinc-300 rounded-md block"
                  : ""
              }
            >
              {description}
            </span>
          </div>
        </div>

        <div>
          {template_type === "Legacy" ? (
            <div className="grid grid-cols-2 divide-x divide-slate-600 divide-solid mt-3 pt-4 border-t border-gray-200">
              <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`
                  );
                }}
                onKeyDown={(e) =>
                  handleEditKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={"Edit Design"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  localStorage.setItem("templateType", template_type);
                  navigate(navigate_url);
                }}
                onKeyDown={(e) =>
                  handleIssueKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={"Issue Certificate"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              {/*
            //! Commented for now(Legacy Revokation)
            <div
              className="flex justify-center items-center group"
              onClick={(e) => {
                e.stopPropagation();
                toggleModal(issuer_credential_template_id);
              }}
            >
              <ToolTip
                component={
                  <DeleteIcon
                    className="fill-white transition-colors duration-500 hover:fill-red-500"
                    height={20}
                    width={20}
                  />
                }
                message={"Delete Draft"}
                tooltipPosition="top-[-3rem]"
                bgColor="#DC2626"
                tooltipWidth={"max-w-[130px]"}
              />
            </div> */}
            </div>
          ) : (
            <div className="grid grid-cols-2 divide-x divide-slate-600 divide-solid mt-3 pt-4 border-t border-gray-200">
              <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/issuer/certificate/edit/${credential_template_id}/${issuer_credential_template_id}/${template_type}`
                  );
                }}
                onKeyDown={(e) =>
                  handleEditKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={"Edit Design"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}`
                  );
                }}
                onKeyDown={(e) =>
                  handleIssueKeyPress(
                    e,
                    credential_template_id,
                    issuer_credential_template_id
                  )
                }
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={"Issue Certificate"}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#3538CD"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              {/* <div
              className="flex justify-center items-center group"
              onClick={(e) => {
                e.stopPropagation();
                toggleModal(issuer_credential_template_id);
              }}
            >
              <ToolTip
                component={
                  <DeleteIcon
                    className="fill-white transition-colors duration-500 hover:fill-red-500"
                    height={20}
                    width={20}
                  />
                }
                message={"Delete Draft"}
                tooltipPosition="top-[-3rem]"
                bgColor="#DC2626"
                tooltipWidth={"max-w-[130px]"}
              />
            </div> */}
            </div>
          )}
        </div>
      </div>
      <DraftsRevokeModal
        isOpen={isModalOpen !== null}
        onClose={() => setIsModalOpen(null)}
        onConfirm={() => revokeCredential(isModalOpen)}
        title="Delete Certificate"
        description="Are you sure you want to delete this certificate?"
      />
    </div>
  );
};
export default ProgrammeBatchCard;
